import type { VariantProps } from 'class-variance-authority';

import { forwardRef } from 'react';
import * as SelectPrimitives from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import { CheckIcon } from '@paid-ui/icons/check';
import { ChevronsUpDownIcon } from '@paid-ui/icons/chevrons-up-down';

const SelectRoot = SelectPrimitives.Root;
export const SelectTrigger = SelectPrimitives.Trigger;
const SelectPortal = SelectPrimitives.Portal;
export const SelectValue = SelectPrimitives.Value;
const SelectIcon = SelectPrimitives.Icon;
const SelectContent = SelectPrimitives.Content;
const SelectViewport = SelectPrimitives.Viewport;
const SelectItemText = SelectPrimitives.ItemText;
const SelectItemIndicator = SelectPrimitives.ItemIndicator;

export const SelectGroup = SelectPrimitives.Group;
export const SelectLabel = SelectPrimitives.Label;

const iconVariants = cva(
  'pointer-events-none absolute inset-y-0 inline-flex h-full shrink-0 select-none items-center justify-center text-base text-grey-dark empty:hidden',
  {
    variants: {
      disabled: {
        true: 'text-grey-tertiary',
      },
      position: {
        prefix: 'left-3.5',
        suffix: 'right-3.5',
      },
    },
  },
);

const selectVariants = cva(
  'relative h-[54px] w-full rounded border border-grey-tertiary bg-white pl-3.5 pr-11 text-left text-black outline-none transition-colors duration-150 ease-linear focus-within:border-blue focus-within:shadow-field disabled:cursor-not-allowed disabled:bg-grey-hover disabled:text-grey-dark data-[state=open]:bg-grey-light data-[placeholder]:text-grey-dark disabled:data-[placeholder]:text-grey-tertiary',
  {
    variants: {
      changed: {
        true: 'border-purple',
      },
      error: {
        true: 'border-pink focus-within:border-pink focus-within:shadow-field-error',
      },
      compact: {
        true: 'h-[44px]',
      },
      prefix: {
        true: 'pl-11',
      },
    },
  },
);

export interface SelectProps
  extends Omit<VariantProps<typeof selectVariants>, 'prefix'>,
    Omit<SelectPrimitives.SelectTriggerProps, 'prefix'> {
  name?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  prefix?: React.ReactNode;
  dropdownClassName?: string;
  sideOffset?: number;
  displayedValue?: React.ReactNode;
  enablePortal?: boolean;
  onValueChange?: (value: string) => void;
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      changed,
      error,
      compact,
      prefix,

      name,
      value,
      defaultValue,
      placeholder,
      readOnly,
      disabled,
      required,
      sideOffset = 0,

      hidden,
      className,
      dropdownClassName,
      children,
      displayedValue,
      enablePortal,

      onValueChange,
      ...restProps
    },
    ref,
  ) => {
    if (hidden) {
      return null;
    }

    const mergedClassName = twMerge(
      selectVariants({
        changed,
        error,
        compact,
        prefix: Boolean(prefix),
        className,
      }),
    );

    return (
      <SelectRoot
        name={name}
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
        required={required}
        open={readOnly ? false : undefined}
        autoComplete="off"
      >
        <SelectTrigger ref={ref} className={mergedClassName} data-name={name} {...restProps}>
          <div className={iconVariants({ disabled, position: 'prefix' })}>{prefix}</div>
          <SelectValue placeholder={placeholder} />
          {displayedValue ? <SelectValue>{displayedValue}</SelectValue> : null}
          <SelectIcon className={iconVariants({ disabled, position: 'suffix' })}>
            <ChevronsUpDownIcon />
          </SelectIcon>
        </SelectTrigger>
        {enablePortal ? (
          <SelectPortal container={document.body}>
            <SelectContent
              position="popper"
              align="start"
              sideOffset={sideOffset}
              collisionPadding={10}
              className={twMerge(
                'z-[1000] max-h-[--radix-select-content-available-height] w-[--radix-select-trigger-width] min-w-[240px] overflow-y-auto rounded-md border border-grey-mid bg-white py-1.5 shadow-md',
                dropdownClassName,
              )}
            >
              <SelectViewport>{children}</SelectViewport>
            </SelectContent>
          </SelectPortal>
        ) : (
          <SelectContent
            position="popper"
            align="start"
            sideOffset={sideOffset}
            collisionPadding={10}
            className={twMerge(
              'z-[500] max-h-[--radix-select-content-available-height] w-[--radix-select-trigger-width] min-w-[240px] overflow-y-auto rounded-md border border-grey-mid bg-white py-1.5 shadow-md',
              dropdownClassName,
            )}
          >
            <SelectViewport>{children}</SelectViewport>
          </SelectContent>
        )}
      </SelectRoot>
    );
  },
);

export type SelectItemProps = SelectPrimitives.SelectItemProps & {
  hideIndicator?: boolean;
};

export const SelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  (
    { value, textValue, disabled, hidden, className, children, hideIndicator, ...restProps },
    ref,
  ) => {
    if (hidden) {
      return null;
    }

    return (
      <SelectPrimitives.Item
        ref={ref}
        value={value}
        textValue={textValue}
        disabled={disabled}
        className={twMerge(
          'relative cursor-pointer py-2.5 pl-4 pr-12 text-base transition-colors duration-150 ease-linear data-[disabled]:cursor-not-allowed data-[disabled]:bg-transparent data-[highlighted]:bg-grey-light data-[disabled]:text-grey-mid',
          className,
        )}
        {...restProps}
      >
        <SelectItemText>{children}</SelectItemText>
        {hideIndicator ? null : (
          <SelectItemIndicator className="absolute inset-y-0 right-4 inline-flex items-center justify-center">
            <CheckIcon className="h-4 w-4 text-blue" />
          </SelectItemIndicator>
        )}
      </SelectPrimitives.Item>
    );
  },
);

type SelectSeparatorProps = SelectPrimitives.SelectSeparatorProps;

export const SelectSeparator: React.FC<SelectSeparatorProps> = ({
  hidden,
  className,
  ...restProps
}) => {
  if (hidden) {
    return null;
  }

  return (
    <SelectPrimitives.Separator
      className={twMerge('h-[1px] w-full bg-grey-mid', className)}
      {...restProps}
    />
  );
};

Select.displayName = 'Select';
SelectItem.displayName = 'SelectItem';
SelectSeparator.displayName = 'SelectSeparator';
